import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Globe, ShieldCheck, TrendUp, UserPlus, Warning } from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString()}`;

export function AnalyticsPanel({ a }) {
  if (!a) return null;
  return (
    <div className="space-y-4" data-testid="analytics-panel">
      <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Cross-border & multi-office analytics</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">Capacity & utilization</h3>
          <div className="space-y-2.5">
            {(a.utilization || []).map((u) => {
              const pct = Math.min(100, Math.round((u.hours_logged / u.capacity) * 100));
              return (
                <div key={u.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-700">{u.staff_name} <span className="text-zinc-400">· {u.location}</span></span>
                    <span className="font-mono text-zinc-600">{u.hours_logged}/{u.capacity}h</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className={`h-full rounded-sm ${pct > 92 ? "bg-rose-500" : pct > 80 ? "bg-amber-500" : "bg-zinc-900"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="offshore-panel">
          <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><Globe size={15} /> Offshore throughput</h3>
          <p className="text-xs text-zinc-500 mb-3">{a.offshore?.hub}</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-zinc-200 p-2"><p className="text-[10px] text-zinc-400">In prep</p><p className="font-mono text-lg">{a.offshore?.returns_in_prep}</p></div>
            <div className="rounded-md border border-zinc-200 p-2"><p className="text-[10px] text-zinc-400">Done this week</p><p className="font-mono text-lg">{a.offshore?.completed_this_week}</p></div>
            <div className="rounded-md border border-zinc-200 p-2"><p className="text-[10px] text-zinc-400">Accuracy</p><p className="font-mono text-lg">{Math.round((a.offshore?.accuracy_rate || 0) * 1000) / 10}%</p></div>
            <div className="rounded-md border border-zinc-200 p-2"><p className="text-[10px] text-zinc-400">Avg cycle</p><p className="font-mono text-lg">{a.offshore?.avg_cycle_days}d</p></div>
          </div>
          {a.offshore?.flags > 0 && <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1"><Warning size={12} /> {a.offshore.flags} accuracy flag open</p>}
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="revenue-tracker">
          <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><TrendUp size={15} /> 1.25x revenue optimization</h3>
          <p className="text-xs text-zinc-500 mb-3">Active client book vs target valuation</p>
          <p className="font-mono text-2xl text-zinc-900">{money(a.revenue_tracker?.active_book_value)}</p>
          <p className="text-xs text-zinc-500 mt-1">YTD book value</p>
          <div className="mt-3 rounded-md bg-zinc-900 text-white p-3">
            <p className="text-[10px] uppercase tracking-wide text-zinc-400">Target @ {a.revenue_tracker?.multiple}x</p>
            <p className="font-mono text-xl">{money(a.revenue_tracker?.target_valuation)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SecurityHeartbeat({ s }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="security-heartbeat">
      <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><ShieldCheck size={15} weight="fill" className="text-emerald-600" /> Security & compliance heartbeat</h3>
      {(s?.anomalies || []).length > 0 && (
        <div className="rounded-md border border-rose-200 bg-rose-50/50 p-2.5 mb-3" data-testid="anomaly-alerts">
          <p className="text-[10px] uppercase tracking-wide text-rose-700 mb-1">Anomalous ingestion alerts</p>
          {(s.anomalies || []).map((d) => (
            <p key={d.id} className="text-xs text-rose-800"><span className="font-mono">{d.filename}</span> — {d.anomaly_reason || "OCR screening failed"}</p>
          ))}
        </div>
      )}
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Active security log stream</p>
      <div className="space-y-1 max-h-52 overflow-y-auto">
        {(s?.events || []).map((e) => (
          <div key={e.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-zinc-100 last:border-0">
            <span className="font-mono text-[9px] uppercase text-zinc-400 w-16 shrink-0">{e.action}</span>
            <span className="text-zinc-600 flex-1 truncate">{e.details}</span>
            <span className="font-mono text-[9px] text-zinc-400 shrink-0">{new Date(e.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralMetrics({ r }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const update = useMutation({
    mutationFn: ({ id, status, bonus_status }) => api.patch(`/referrals/${id}`, { status, bonus_status }),
    onSuccess: () => { toast.success("Referral updated"); qc.invalidateQueries({ queryKey: ["command-center"] }); },
    onError: (e) => toast.error(errText(e)),
    onSettled: () => setBusy(false),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="referral-metrics">
      <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><UserPlus size={15} /> Referral program</h3>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {[["Invited", r?.total], ["Joined", r?.joined], ["Engaged", r?.engaged], ["Bonus paid", money(r?.bonus_paid)]].map(([l, v]) => (
          <div key={l} className="rounded-md border border-zinc-200 p-2 text-center"><p className="text-[10px] text-zinc-400">{l}</p><p className="font-mono text-sm">{v ?? 0}</p></div>
        ))}
      </div>
      <div className="space-y-1.5">
        {(r?.recent || []).map((x) => (
          <div key={x.id} className="flex items-center gap-2 text-xs py-1 border-b border-zinc-100 last:border-0" data-testid={`referral-row-${x.id}`}>
            <span className="text-zinc-700 flex-1 truncate">{x.referred_name} <span className="text-zinc-400">by {x.referrer_name}</span></span>
            <span className="text-[10px] uppercase bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">{x.status}</span>
            {x.status === "invited" && <button data-testid={`referral-joined-btn-${x.id}`} disabled={busy} onClick={() => { setBusy(true); update.mutate({ id: x.id, status: "joined" }); }} className="text-[10px] text-zinc-600 border border-zinc-200 rounded-sm px-1.5 py-0.5 hover:bg-zinc-50">Mark joined</button>}
            {x.status === "joined" && <button data-testid={`referral-engaged-btn-${x.id}`} disabled={busy} onClick={() => { setBusy(true); update.mutate({ id: x.id, status: "engaged", bonus_status: "paid" }); }} className="text-[10px] text-emerald-700 border border-emerald-200 rounded-sm px-1.5 py-0.5 hover:bg-emerald-50">Engaged → pay bonus</button>}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-400 mt-2">Bonus pending: <span className="font-mono">{money(r?.bonus_pending)}</span></p>
    </div>
  );
}
